import { formatSecondsToHHMMSS } from "@utils/utils";
import { format, parseISO } from "date-fns";
import { useEffect, useState } from "react";

import { CalendarDateRange, Column, SearchableDropdownItem } from "@fronterahealth/frontera-ui-components";

import { useGetQuestionsAskedByRbtRawDataQuery } from "@api/graphql/types-and-hooks";
import { DialogFilterBar } from "@pages/VideoAnalytics/components/DialogFilterBar";
import { RawDataTableWrapper } from "@pages/VideoAnalytics/components/RawDataTableWrapper";
import { DATE_FORMAT, PAGE_SIZE } from "@pages/VideoAnalytics/helpers";
import { useVideoAnalytics } from "@providers/VideoAnalyticsProvider";

interface AvgQuestionAskedRawDataType {
  rbtName: string;
  sessionDatetime: string;
  sessionDuration: number;
  totalQuestionsAsked: number;
}
export const AvgQuestionAskedRawData = () => {
  const { selectedRBTDates, selectedBCBA: selectedBCBAList } = useVideoAnalytics();

  const [selectedRBT, setSelectedRBT] = useState<SearchableDropdownItem[]>(selectedBCBAList);
  const [dates, setDates] = useState<CalendarDateRange | undefined>(selectedRBTDates);
  const [selectedClient, setSelectedClient] = useState<SearchableDropdownItem[]>([]);

  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);

  useEffect(() => {
    setPageNo(1);
  }, [dates, selectedRBT, selectedClient]);

  const { data, isFetching } = useGetQuestionsAskedByRbtRawDataQuery(
    {
      fromDate: dates?.startDate ? format(dates.startDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      toDate: dates?.endDate ? format(dates.endDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      rbtProviderIds: selectedRBT.map((item) => item.id),
      learnerIds: selectedClient.map((item) => item.id),
      offset: pageNo * pageSize - pageSize,
      count: pageSize,
    },
    {
      queryKey: ["getQuestionsAskedByRbtRawData", dates, selectedRBT, pageNo, pageSize, selectedClient],
    },
  );
  const columns: Column<AvgQuestionAskedRawDataType>[] = [
    {
      title: "RBT",
      key: "rbtName",
      dataIndex: "rbtName",
      render: (value) => value || "-",
    },
    {
      title: "Session Date & Time",
      key: "sessionDatetime",
      dataIndex: "sessionDatetime",
      render: (value) => {
        if (value) {
          const parsedDate = parseISO(value as string);
          const formattedDate = format(parsedDate, "yyyy-MM-dd HH:mm");
          return formattedDate;
        }
        return "-";
      },
    },
    {
      title: "Session Duration",
      key: "sessionDuration",
      dataIndex: "sessionDuration",
      render: (value: string | number) => (value ? formatSecondsToHHMMSS(value as number) : "-"),
    },
    {
      title: "Total Question Asked",
      key: "totalQuestionsAsked",
      dataIndex: "totalQuestionsAsked",
      render: (_, record) => record.totalQuestionsAsked ?? 0,
    },
  ];
  return (
    <div className="grid space-y-2">
      <DialogFilterBar
        dates={dates}
        setDates={setDates}
        selectedRBTs={selectedRBT}
        setSelectedRBTs={setSelectedRBT}
        selectedClients={selectedClient}
        setSelectedClients={setSelectedClient}
        showBCBAFilter={false}
      />
      <RawDataTableWrapper
        data={
          data && data.getQuestionsAskedByRbtRawData?.rawData
            ? (data.getQuestionsAskedByRbtRawData?.rawData as AvgQuestionAskedRawDataType[])
            : []
        }
        columns={columns}
        loading={isFetching}
        totalItemCount={data?.getQuestionsAskedByRbtRawData?.totalRecordsCount || 0}
        defaultItemsPerPage={pageSize}
        currentPage={pageNo}
        onPageChange={(current, itemsPerPage) => {
          setPageNo(current);
          setPageSize(itemsPerPage);
        }}
      />
    </div>
  );
};
